import { useContext, useCallback, useMemo } from 'react';
import { createElement } from 'react';
import { ValidationContext } from './ValidationContext';

const NOOP = () => {};

export function useFieldValidation(fieldKey: string) {
  const ctx = useContext(ValidationContext);
  if (!ctx) throw new Error('useFieldValidation must be used within ValidationProvider');

  const error = ctx.getFieldError(fieldKey);

  const onBlur = useCallback(() => {
    ctx.markFieldBlurred(fieldKey);
  }, [ctx, fieldKey]);

  const errorElement = useMemo(() => {
    if (!error) return null;
    return createElement(
      'p',
      {
        id: `${fieldKey}-error`,
        className: 'font-sans text-xs text-destructive mt-1',
        'data-testid': `${fieldKey}-error`,
      },
      error
    );
  }, [error, fieldKey]);

  return {
    error,
    errorElement,
    errorId: error ? `${fieldKey}-error` : undefined,
    onBlur,
    hasError: !!error,
  };
}

/**
 * Safe variant of useFieldValidation for components that render in BOTH
 * ValidationProvider-wrapped contexts (onboarding) and legacy contexts
 * (profile) that don't have a provider yet.
 *
 * Returns the same shape as useFieldValidation. When no ValidationProvider
 * is present, all values are inert (no error, no-op onBlur).
 */
export function useOptionalFieldValidation(fieldKey: string) {
  const ctx = useContext(ValidationContext);

  const error = ctx ? ctx.getFieldError(fieldKey) : null;

  const onBlur = useCallback(() => {
    ctx?.markFieldBlurred(fieldKey);
  }, [ctx, fieldKey]);

  const errorElement = useMemo(() => {
    if (!error) return null;
    return createElement(
      'p',
      {
        id: `${fieldKey}-error`,
        className: 'font-sans text-xs text-destructive mt-1',
        'data-testid': `${fieldKey}-error`,
      },
      error
    );
  }, [error, fieldKey]);

  return {
    error,
    errorElement,
    errorId: error ? `${fieldKey}-error` : undefined,
    onBlur: ctx ? onBlur : NOOP,
    hasError: !!error,
    hasProvider: !!ctx,
  };
}
